import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiDroplet, FiCpu, FiHome, FiUsers, FiBarChart2, FiTruck, FiWind } from 'react-icons/fi';
import './AdvisoryScope.css';

// Import images (make sure these image files exist in your assets folder)
import energyImage from './pic1.jpeg';
import commoditiesImage from './pic2.jpg';
import realEstateImage from './pic3.jpg';
import jointVenturesImage from './pic4.png';
import energyTransitionImage from './pic5.jpg';
import infrastructureImage from './pic6.jpg';
import strategicMineralsImage from './pic7.png';

const AdvisoryScope = () => {
  const sectors = [
    {
      id: 'energy',
      icon: <FiDroplet />,
      title: 'Energy & Oil',
      description: 'Strategic guidance for upstream, midstream, and downstream energy, including JVs, acquisitions, and structured transactions.',
      image: energyImage,
      imageAlt: 'Offshore oil platform at sunset',
      capabilities: [
        'Upstream exploration partnerships',
        'Production-sharing agreements',
        'Midstream infrastructure development',
        'Downstream refining and marketing',
        'Energy transition strategies',
        'Cross-border energy transactions'
      ]
    },
    {
      id: 'commodities',
      icon: <FiBarChart2 />,
      title: 'Commodities & Mining',
      description: 'Advisory for commodity-linked investments and infrastructure projects, focusing on logistics, supply chain, and strategic market positioning.',
      image: commoditiesImage,
      imageAlt: 'Open pit mining operation',
      capabilities: [
        'Strategic minerals and metals',
        'Battery-grade materials',
        'Agricultural and soft commodities',
        'Offtake arrangements',
        'Supply chain coordination',
        'Warehouse financing'
      ]
    },
    {
      id: 'real-estate',
      icon: <FiHome />,
      title: 'Real Estate & Real Assets',
      description: 'Institutional real estate advisory for commercial, industrial, and mixed-use developments, specializing in cross-border transactions and asset structuring.',
      image: realEstateImage,
      imageAlt: 'Modern commercial skyscraper',
      capabilities: [
        'Commercial and industrial assets',
        'Portfolio diversification',
        'Cross-border real estate platforms',
        'Development and value-add',
        'Public-private partnerships',
        'Asset structuring'
      ]
    },
    {
      id: 'joint-ventures',
      icon: <FiUsers />,
      title: 'Joint Ventures',
      description: 'Facilitating multi-party partnerships and consortiums, structuring complex deals across industries and geographies with capital partners.',
      image: jointVenturesImage,
      imageAlt: 'Business partnership handshake',
      capabilities: [
        'Cross-border partnerships',
        'Equity participation structures',
        'Co-investment platforms',
        'Governance frameworks',
        'Jurisdiction selection',
        'Transaction sequencing'
      ]
    }
  ];

  const additionalServices = [
    {
      icon: <FiCpu />,
      title: 'Energy Transition',
      description: 'Strategic advisory for renewable energy, clean technology, and sustainable infrastructure investments.',
      image: energyTransitionImage,
      imageAlt: 'Wind turbines and solar panels'
    },
    {
      icon: <FiTruck />,
      title: 'Infrastructure',
      description: 'Project finance and development advisory for large-scale infrastructure initiatives.',
      image: infrastructureImage,
      imageAlt: 'Modern bridge infrastructure'
    },
    {
      icon: <FiWind />,
      title: 'Strategic Minerals',
      description: 'Specialized advisory for rare earth elements and critical materials supply chains.',
      image: strategicMineralsImage,
      imageAlt: 'Rare earth mineral samples'
    }
  ];

  return (
    <div className="advisory-scope">
      {/* Hero Section */}
      <section className="scope-hero">
        <div className="container">
          <h1 className="scope-hero-title">Advisory Capabilities</h1>
          <p className="scope-hero-subtitle">
            We provide independent advisory across four primary asset classes, structuring and 
            facilitating deals without direct ownership or promotion. Engagements are tailored and discreet.
          </p>
        </div>
      </section>

      {/* Core Sectors */}
      <section className="sectors-section">
        <div className="container">
          {sectors.map((sector, index) => (
            <div key={sector.id} id={sector.id} className={`sector-block ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="sector-content fade-in">
                <div className="sector-icon">{sector.icon}</div>
                <h2 className="sector-title">{sector.title}</h2>
                <p className="sector-description">{sector.description}</p>
                <div className="capabilities-list">
                  {sector.capabilities.map((capability, i) => (
                    <div key={i} className="capability-item">
                      <FiArrowRight className="capability-icon" />
                      <span>{capability}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sector-visual slide-in-right">
                <img 
                  src={sector.image} 
                  alt={sector.imageAlt}
                  className="sector-image"
                  loading="lazy"
                />
                <div className="image-overlay"></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="additional-section">
        <div className="container">
          <h2 className="section-title center">Specialized Advisory</h2>
          <p className="section-subtitle center">
            Cross-sector expertise for emerging opportunities
          </p>
          <div className="additional-grid">
            {additionalServices.map((service, index) => (
              <div key={index} className="additional-card scale-in">
                <div className="additional-image-wrapper">
                  <img 
                    src={service.image} 
                    alt={service.imageAlt}
                    className="additional-image"
                    loading="lazy"
                  />
                  <div className="additional-icon-overlay">
                    {service.icon}
                  </div>
                </div>
                <div className="additional-content">
                  <h3 className="additional-title">{service.title}</h3>
                  <p className="additional-description">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Framework */}
      <section className="framework-section">
        <div className="container">
          <div className="framework-box">
            <h2>Independent Advisory Framework</h2>
            <p>
              Innova Core provides independent intermediary services, connecting principals 
              with aligned opportunities. Our confidential, relationship-driven approach adheres 
              to institutional standards for sophisticated clients, without proprietary interests.
            </p>
            <div className="framework-features">
              <div className="feature">
                <span className="feature-number">01</span>
                <h4>No Proprietary Positions</h4>
                <p>We maintain independence without conflicts of interest</p>
              </div>
              <div className="feature">
                <span className="feature-number">02</span>
                <h4>Institutional Standards</h4>
                <p>Rigorous due diligence and structuring protocols</p>
              </div>
              <div className="feature">
                <span className="feature-number">03</span>
                <h4>Global Expertise</h4>
                <p>Cross-border transaction capabilities across jurisdictions</p>
              </div>
            </div>
            <Link to="/engagement-process" className="btn-primary">
              Explore Our Process
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="scope-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Discuss Your Strategic Objectives</h2>
            <p>Contact our team for a confidential discussion about your advisory needs</p>
            <Link to="/contact" className="btn-primary">
              Schedule Consultation
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvisoryScope;